import React from 'react';
import {
  MapPin, Home, Heart, Filter, User, Menu, X, Check,
  ChevronRight, ChevronLeft, ChevronDown, ChevronUp,
  Search, Star, Bed, Bath, Car, Wifi, Dog, Dumbbell,
  Building, Calendar, DollarSign, Phone, Mail, ExternalLink,
  Share2, Bookmark, BookmarkCheck, Eye, EyeOff,
  Sun, Moon, Settings, LogOut, Plus, Minus, Trash2, Edit,
  RefreshCw, Loader2, AlertCircle, CheckCircle, Info, XCircle,
  Map, List, Grid, SlidersHorizontal, ArrowUpDown,
  Footprints, Bike, Bus, TreePine, Coffee, Utensils,
  Sparkles, Zap, TrendingUp, Award, Play, LayoutDashboard, LogIn,
  BarChart3, Users, Shield, Copy, Upload, Download, Save, Pencil, UserCheck,
  Layers, AlertTriangle, ArrowRight, Pin, Tag, FileText, ArrowLeft,
  PlusCircle, FlaskConical, Globe, Columns3, Activity, HelpCircle,
  GitCompareArrows, Waves, Cpu, Briefcase, Rocket, Music, GraduationCap,
  ShoppingCart, Lock, VolumeX, ZoomIn,
  MailCheck, MailX, MessageSquareText, MessageSquare, Send,
  ArrowUp, Trees, MessageCircle,
  History, TestTubes, RotateCcw,
  Flag, HeartPulse,
  Shirt, ChefHat, Smartphone, Sofa, Gem, Sunset, Laptop, Bell,
  Package, Flame, Snowflake, Target, Mountain,
  LayoutGrid,
  ThumbsUp, ThumbsDown,
  Compass, ClipboardList, Code,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const iconMap = {
  // Navigation
  'map-pin': MapPin,
  'home': Home,
  'heart': Heart,
  'filter': Filter,
  'user': User,
  'menu': Menu,
  'x': X,
  'check': Check,
  'chevron-right': ChevronRight,
  'chevron-left': ChevronLeft,
  'chevron-down': ChevronDown,
  'chevron-up': ChevronUp,
  'search': Search,
  
  // Apartment features
  'star': Star,
  'bed': Bed,
  'bath': Bath,
  'car': Car,
  'wifi': Wifi,
  'dog': Dog,
  'dumbbell': Dumbbell,
  'building': Building,
  'calendar': Calendar,
  'dollar': DollarSign,
  'phone': Phone,
  'mail': Mail,
  'external-link': ExternalLink,
  
  // Actions
  'share': Share2,
  'share-2': Share2,
  'bookmark': Bookmark,
  'bookmark-check': BookmarkCheck,
  'eye': Eye,
  'eye-off': EyeOff,
  'sun': Sun,
  'moon': Moon,
  'settings': Settings,
  'logout': LogOut,
  'log-out': LogOut,
  'log-in': LogIn,
  'plus': Plus,
  'plus-circle': PlusCircle,
  'minus': Minus,
  'trash': Trash2,
  'trash-2': Trash2,
  'edit': Edit,
  'refresh': RefreshCw,
  'loader': Loader2,
  'play': Play,
  
  // Status
  'alert': AlertCircle,
  'success': CheckCircle,
  'info': Info,
  'check-circle': CheckCircle,
  'zoom-in': ZoomIn,
  'error': XCircle,
  
  // Views
  'map': Map,
  'list': List,
  'grid': Grid,
  'sliders': SlidersHorizontal,
  'sort': ArrowUpDown,
  'layout-dashboard': LayoutDashboard,
  
  // Lifestyle
  'walk': Footprints,
  'footprints': Footprints,
  'bike': Bike,
  'bus': Bus,
  'nature': TreePine,
  'tree-pine': TreePine,
  'coffee': Coffee,
  'food': Utensils,
  'utensils': Utensils,
  'waves': Waves,
  'music': Music,
  'graduation-cap': GraduationCap,
  'shopping-cart': ShoppingCart,
  'lock': Lock,
  'volume-x': VolumeX,
  'cpu': Cpu,
  'briefcase': Briefcase,
  'rocket': Rocket,
  'arrow-up': ArrowUp,
  'trees': Trees,
  'message-circle': MessageCircle,
  
  // AI/Special
  'sparkles': Sparkles,
  'zap': Zap,
  'trending': TrendingUp,
  'trending-up': TrendingUp,
  'award': Award,
  'git-compare': GitCompareArrows,

  // Admin
  'bar-chart-3': BarChart3,
  'bar-chart': BarChart3,
  'users': Users,
  'user-check': UserCheck,

  // Amenity icons
  'shirt': Shirt,
  'chef-hat': ChefHat,
  'smartphone': Smartphone,
  'sofa': Sofa,
  'gem': Gem,
  'sunset': Sunset,
  'laptop': Laptop,
  'bell': Bell,
  'package': Package,
  'flame': Flame,
  'snowflake': Snowflake,
  'target': Target,
  'mountain': Mountain,
  'shield': Shield,
  'copy': Copy,
  'upload': Upload,
  'download': Download,
  'save': Save,
  'pencil': Pencil,
  'alert-circle': AlertCircle,
  'refresh-cw': RefreshCw,
  'layers': Layers,
  'alert-triangle': AlertTriangle,
  'mail-check': MailCheck,
  'mail-x': MailX,
  'message-square-check': MessageSquareText,
  'arrow-right': ArrowRight,
  'arrow-left': ArrowLeft,
  'pin': Pin,
  'tag': Tag,
  'file-text': FileText,
  'dollar-sign': DollarSign,
  'flask-conical': FlaskConical,
  'globe': Globe,
  'columns': Columns3,
  'activity': Activity,
  'help-circle': HelpCircle,
  
  // Vibe Eval icons
  'history': History,
  'test-tubes': TestTubes,
  'sliders-horizontal': SlidersHorizontal,
  'loader-2': Loader2,
  'rotate-ccw': RotateCcw,
  
  // Tuning Chat icons
  'message-square': MessageSquare,
  'send': Send,

  // QA icons
  'flag': Flag,
  'heart-pulse': HeartPulse,
  
  // Feedback icons
  'thumbs-up': ThumbsUp,
  'thumbs-down': ThumbsDown,
  
  // Layout icons
  'layout': LayoutGrid,
  'layout-grid': LayoutGrid,
  'compass': Compass,
  'clipboard-list': ClipboardList,
  'code': Code,
};

type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface IconProps extends React.SVGAttributes<SVGElement> {
  name: string;
  size?: IconSize | number;
  className?: string;
}

/**
 * Icon atom - Unified icon component using Lucide
 */
export function Icon({ 
  name, 
  size = 'md', 
  className,
  ...props 
}: IconProps) {
  const IconComponent = iconMap[name as keyof typeof iconMap];
  
  if (!IconComponent) {
    console.warn(`Icon "${name}" not found`);
    return null;
  }
  
  const sizeClasses: Record<string, string> = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
    xl: 'h-8 w-8',
  };
  
  if (typeof size === 'number') {
    return (
      <IconComponent 
        className={className}
        style={{ width: size, height: size }}
        {...props}
      />
    );
  }
  
  return (
    <IconComponent 
      className={cn(sizeClasses[size], className)} 
      {...props}
    />
  );
}

// Export all icon names for autocomplete
export const iconNames = Object.keys(iconMap);
